import {
  PasswordInput as MantinePasswordInput,
  PasswordInputProps as MantinePasswordInputProps,
} from '@mantine/core';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { formatSsn, normalizeAndSetSSN } from '@zorro/common-utils';
import clsx from 'clsx';
import { forwardRef, useState } from 'react';

import styles from './SsnPasswordInput.module.css';

export type SsnPasswordInputSize = 'md' | 'lg';

// Prop name `reveal` is dictated by Mantine's visibilityToggleIcon API
const VisibilityToggleIcon = ({ reveal }: { reveal: boolean }) =>
  reveal ? (
    <IconEyeOff size={16} stroke={1.5} />
  ) : (
    <IconEye size={16} stroke={1.5} />
  );

export interface SsnPasswordInputProps extends Omit<
  MantinePasswordInputProps,
  | 'variant'
  | 'color'
  | 'styles'
  | 'classNames'
  | 'style'
  | 'size'
  | 'value'
  | 'onChange'
  | 'readOnly'
> {
  /** Input size: 'md' (40px) or 'lg' (48px). Default: 'md' */
  size?: SsnPasswordInputSize;
  /** Whether SSN is initially visible (uncontrolled). Default: false */
  // eslint-disable-next-line no-restricted-syntax -- name inherited from Mantine
  defaultVisible?: boolean;
  /** SSN value (raw digits, no dashes) */
  value?: string;
  /** Change handler — compatible with react-hook-form Controller */
  onChange: (...event: unknown[]) => void;
  /** Add 'fs-mask' class for FullStory masking of sensitive data */
  isMasked?: boolean;
  isReadOnly?: boolean;
}

export const SsnPasswordInput = forwardRef<
  HTMLInputElement,
  SsnPasswordInputProps
>(
  (
    {
      size = 'md',
      value,
      onChange,
      defaultVisible,
      error,
      disabled,
      isReadOnly,
      isMasked = false,
      ...props
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(defaultVisible ?? false);
    const displayValue = isVisible ? formatSsn(value) : value;

    return (
      <MantinePasswordInput
        ref={ref}
        unstyled
        classNames={{
          root: styles.root,
          wrapper: styles.wrapper,
          input: styles.input,
          innerInput: clsx(styles.innerInput, isMasked && 'fs-mask'),
          section: styles.section,
          label: styles.label,
          error: styles.error,
          required: styles.required,
          description: styles.description,
          visibilityToggle: styles.visibilityToggle,
        }}
        wrapperProps={{ 'data-input-size': size }}
        visible={isVisible}
        onVisibilityChange={setIsVisible}
        visibilityToggleIcon={VisibilityToggleIcon}
        value={displayValue}
        onChange={(event) => normalizeAndSetSSN(event.target.value, onChange)}
        placeholder="XXX-XX-XXXX"
        tabIndex={0}
        error={error}
        disabled={disabled}
        readOnly={isReadOnly}
        {...props}
      />
    );
  }
);

SsnPasswordInput.displayName = 'SsnPasswordInput';
